import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { ThemeToggle } from "./theme-toggle";
import {
  Plus,
  Building2,
  Users,
  DollarSign,
  TrendingUp,
  Edit,
  Trash2,
  Play,
  Calendar,
  Target,
  BarChart3,
  Sparkles,
  Rocket,
  CheckCircle,
  Clock,
  ArrowRight,
} from "lucide-react";

interface Startup {
  id: string;
  name: string;
  description?: string;
  status: "draft" | "in-progress" | "completed";
  createdAt: Date;
  updatedAt: Date;
  data?: {
    companyProfile?: any;
    teamRoles?: any[];
    operatingCosts?: any[];
    investmentScenario?: any;
    financials?: any;
  };
  metrics?: {
    teamSize?: number;
    monthlyRevenue?: number;
    totalCosts?: number;
    runway?: number;
    valuation?: number;
  };
}

const StartupDashboard: React.FC = () => {
  const [startups, setStartups] = useState<Startup[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newStartupName, setNewStartupName] = useState("");
  const [newStartupDescription, setNewStartupDescription] = useState("");

  // Load startups from localStorage on mount
  useEffect(() => {
    const savedStartups = localStorage.getItem("userStartups");
    if (savedStartups) {
      try {
        const parsed = JSON.parse(savedStartups);
        const startupsWithDates = parsed.map((startup: any) => ({
          ...startup,
          createdAt: new Date(startup.createdAt),
          updatedAt: new Date(startup.updatedAt),
        }));
        setStartups(startupsWithDates);
      } catch (error) {
        console.error("Error loading startups:", error);
      }
    }
    setIsLoaded(true);
  }, []);

  // Save startups to localStorage whenever they change
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem("userStartups", JSON.stringify(startups));
    }
  }, [startups, isLoaded]);

  const createNewStartup = () => {
    if (!newStartupName.trim() || isCreating) return;
    setIsCreating(true);

    const newStartup: Startup = {
      id: Date.now().toString(),
      name: newStartupName.trim(),
      description: newStartupDescription.trim() || undefined,
      status: "draft",
      createdAt: new Date(),
      updatedAt: new Date(),
      data: {
        companyProfile: {
          name: newStartupName.trim(),
          description: newStartupDescription.trim() || "",
          industry: "",
          location: "",
        },
      },
    };

    setStartups((prev) => [...prev, newStartup]);
    setNewStartupName("");
    setNewStartupDescription("");
    setIsCreateDialogOpen(false);

    // Navigate to onboarding with the new startup ID
    window.location.href = `/new?startupId=${newStartup.id}`;
  };

  const deleteStartup = (startupId: string) => {
    setStartups((prev) => prev.filter((s) => s.id !== startupId));
  };

  const getStatusColor = (status: Startup["status"]) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
      case "in-progress":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
      case "draft":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300";
    }
  };

  const getStatusIcon = (status: Startup["status"]) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4" />;
      case "in-progress":
        return <Clock className="w-4 h-4" />;
      case "draft":
        return <Edit className="w-4 h-4" />;
      default:
        return <Edit className="w-4 h-4" />;
    }
  };

  const getCompletionPercentage = (startup: Startup) => {
    if (startup.status === "completed") return 100;
    if (startup.status === "draft") return 0;

    // Calculate based on available data
    let completed = 0;
    const total = 5; // company, team, costs, investment, financials

    if (startup.data?.companyProfile) completed++;
    if (startup.data?.teamRoles?.length) completed++;
    if (startup.data?.operatingCosts?.length) completed++;
    if (startup.data?.investmentScenario) completed++;
    if (startup.data?.financials) completed++;

    return Math.round((completed / total) * 100);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background">
        <div className="container flex h-16 items-center justify-between px-6">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <a href="/" className="flex items-center space-x-2">
                <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
                  <span className="text-primary-foreground font-bold text-sm">
                    S
                  </span>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-foreground">
                    Startup Builder
                  </h1>
                </div>
              </a>
            </div>

            {/* Navigation Links */}
            <nav className="flex items-center space-x-4">
              <a
                href="/new"
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                New Project
              </a>
            </nav>
          </div>

          <div className="flex items-center">
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Hero Section for New Users */}
        {startups.length === 0 && (
          <div className="text-center space-y-8 mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-500 rounded-full mb-4">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <div className="space-y-4">
              <h1 className="text-4xl font-bold text-blue-600 dark:text-blue-400">
                Build Your Startup Empire
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Create, manage, and analyze multiple SaaS startups with
                professional investment modeling and financial projections.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Dialog
                open={isCreateDialogOpen}
                onOpenChange={setIsCreateDialogOpen}
              >
                <DialogTrigger asChild>
                  <Button size="lg" className="gap-2 px-8 py-3 text-lg">
                    <Plus className="w-5 h-5" />
                    Start Your First Startup
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Startup</DialogTitle>
                    <DialogDescription>
                      Give your startup a name and optional description to get
                      started.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="startup-name"
                        className="text-sm font-medium"
                      >
                        Startup Name *
                      </label>
                      <Input
                        id="startup-name"
                        placeholder="My Awesome SaaS"
                        value={newStartupName}
                        onChange={(e) => setNewStartupName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="startup-description"
                        className="text-sm font-medium"
                      >
                        Description (Optional)
                      </label>
                      <Input
                        id="startup-description"
                        placeholder="Brief description of your startup idea"
                        value={newStartupDescription}
                        onChange={(e) =>
                          setNewStartupDescription(e.target.value)
                        }
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setIsCreateDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={createNewStartup}
                        disabled={!newStartupName.trim()}
                      >
                        Create Startup
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              <Button
                variant="outline"
                size="lg"
                className="gap-2"
                onClick={() => setIsCreateDialogOpen(true)}
              >
                <Play className="w-5 h-5" />
                Quick Start
              </Button>
            </div>
          </div>
        )}

        {/* Startups Grid */}
        {startups.length > 0 && (
          <div className="space-y-8">
            {/* Header with Create Button */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Your Startups</h2>
                <p className="text-muted-foreground">
                  Manage and analyze your startup portfolio
                </p>
              </div>

              <Dialog
                open={isCreateDialogOpen}
                onOpenChange={setIsCreateDialogOpen}
              >
                <DialogTrigger asChild>
                  <Button className="gap-2">
                    <Plus className="w-4 h-4" />
                    New Startup
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Startup</DialogTitle>
                    <DialogDescription>
                      Give your startup a name and optional description to get
                      started.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="startup-name"
                        className="text-sm font-medium"
                      >
                        Startup Name *
                      </label>
                      <Input
                        id="startup-name"
                        placeholder="My Awesome SaaS"
                        value={newStartupName}
                        onChange={(e) => setNewStartupName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="startup-description"
                        className="text-sm font-medium"
                      >
                        Description (Optional)
                      </label>
                      <Input
                        id="startup-description"
                        placeholder="Brief description of your startup idea"
                        value={newStartupDescription}
                        onChange={(e) =>
                          setNewStartupDescription(e.target.value)
                        }
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setIsCreateDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={createNewStartup}
                        disabled={!newStartupName.trim()}
                      >
                        Create Startup
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Startups Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {startups.map((startup) => (
                <Card
                  key={startup.id}
                  className="hover:shadow-lg transition-shadow"
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <CardTitle className="text-lg">
                          {startup.name}
                        </CardTitle>
                        {startup.description && (
                          <CardDescription className="text-sm">
                            {startup.description}
                          </CardDescription>
                        )}
                      </div>
                      <Badge
                        className={`gap-1 ${getStatusColor(startup.status)}`}
                      >
                        {getStatusIcon(startup.status)}
                        {startup.status}
                      </Badge>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Progress Bar */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          Completion
                        </span>
                        <span className="font-medium">
                          {getCompletionPercentage(startup)}%
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${getCompletionPercentage(startup)}%`,
                          }}
                        />
                      </div>
                    </div>

                    {/* Metrics */}
                    {startup.metrics && (
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {startup.metrics.teamSize && (
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            <span>{startup.metrics.teamSize} people</span>
                          </div>
                        )}
                        {startup.metrics.monthlyRevenue && (
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-muted-foreground" />
                            <span>
                              ${startup.metrics.monthlyRevenue.toLocaleString()}
                              /mo
                            </span>
                          </div>
                        )}
                        {startup.metrics.valuation && (
                          <div className="flex items-center gap-2">
                            <Target className="w-4 h-4 text-muted-foreground" />
                            <span>
                              ${startup.metrics.valuation.toLocaleString()}
                            </span>
                          </div>
                        )}
                        {startup.metrics.runway && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            <span>{startup.metrics.runway}mo runway</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      {startup.status === "completed" ? (
                        <Button
                          className="flex-1 gap-2"
                          onClick={() =>
                            (window.location.href = `/?startupId=${startup.id}`)
                          }
                        >
                          <BarChart3 className="w-4 h-4" />
                          View Analysis
                        </Button>
                      ) : (
                        <Button
                          className="flex-1 gap-2"
                          onClick={() =>
                            (window.location.href = `/new?startupId=${startup.id}`)
                          }
                        >
                          <Edit className="w-4 h-4" />
                          Continue Setup
                        </Button>
                      )}

                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Are you sure you want to delete "${startup.name}"? This action cannot be undone.`,
                            )
                          ) {
                            deleteStartup(startup.id);
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Last Updated */}
                    <div className="text-xs text-muted-foreground pt-2 border-t">
                      Updated {startup.updatedAt.toLocaleDateString()}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Features Section */}
        <div className="mt-16 space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-4">
              Everything You Need to Build
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From idea validation to investment-ready business plans, our
              comprehensive toolkit guides you through every step.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="font-semibold mb-2">Company Setup</h3>
                <p className="text-sm text-muted-foreground">
                  Define your business model, target market, and competitive
                  landscape.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="font-semibold mb-2">Team Building</h3>
                <p className="text-sm text-muted-foreground">
                  Plan your hiring strategy and build a world-class team
                  structure.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="font-semibold mb-2">Financial Modeling</h3>
                <p className="text-sm text-muted-foreground">
                  Create detailed financial projections and investment
                  scenarios.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default StartupDashboard;
